"use client";

import React from "react";
import { Activity, ArrowRight, ChevronRight, CircleCheck as CheckCircle2, Cpu, FileText, Layers, Sparkles, Waves } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const modalities = [{
  id: "nir",
  title: "NIR Spectroscopy",
  range: "780 – 2500 nm",
  applications: "Moisture, blend uniformity, drying endpoint & raw material ID",
  status: "Validated & Supported"
}, {
  id: "raman",
  title: "Raman Spectroscopy",
  range: "100 – 3400 cm⁻¹",
  applications: "Polymorph screening, reaction monitoring & non-destructive ID",
  status: "Validated & Supported"
}, {
  id: "uv-vis",
  title: "UV-Vis Spectroscopy",
  range: "190 – 1100 nm",
  applications: "Concentration dynamics, dissolution & protein quantification",
  status: "Validated & Supported"
}, {
  id: "ftir",
  title: "FTIR Spectroscopy",
  range: "4000 – 400 cm⁻¹",
  applications: "Functional group verification, polymer profiling & QC release",
  status: "Validated & Supported"
}];
export default function ChemometricHero() {
  return <section id="chemometrichero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                Services
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span aria-current="page" className="font-medium text-foreground">
                Chemometric Model Development
              </span>
            </li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Left Column: 8 Cols on desktop */}
          <div className="lg:col-span-8 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs text-secondary-foreground border border-border">
                <Sparkles className="size-3 text-primary mr-1" />
                Regulated Spectroscopy Services
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs text-muted-foreground border-border">
                Ireland &amp; UK
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
              Chemometric Model Development &amp; Multivariate Spectral Analytics
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl">
              Application-led quantitative calibration, qualitative classification models, and continuous lifecycle maintenance for regulated laboratories and manufacturing teams across Ireland and the UK.
            </p>

            {/* Dual CTA buttons */}
            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Button asChild size="lg" className="font-semibold shadow-sm">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-semibold border-border">
                <Link to="/FeasibilityStudiesPage">
                  Request Feasibility Study
                </Link>
              </Button>
            </div>

            {/* Quick summary metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground text-sm">GAMP 5 &amp; Data Integrity</p>
                  <p className="text-xs text-muted-foreground">Full audit trail &amp; model qualification</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Layers className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground text-sm">PLS, PCA &amp; SIMCA</p>
                  <p className="text-xs text-muted-foreground">Advanced multivariate algorithms</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Activity className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground text-sm">Lifecycle Verification</p>
                  <p className="text-xs text-muted-foreground">Drift monitoring &amp; model updates</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: 4 Cols on desktop - Technical Matrix Card */}
          <div className="lg:col-span-4">
            <Card className="shadow-md border border-border bg-card">
              <CardHeader className="pb-3 border-b border-border">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
                    <Cpu className="size-4" />
                    Spectral Matrix
                  </span>
                  <Badge variant="outline" className="text-[11px] py-0 px-2 font-mono">
                    21 CFR Part 11
                  </Badge>
                </div>
                <CardTitle className="text-lg font-bold text-card-foreground mt-2">
                  Supported Modalities
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Multi-vendor instrument chemometrics &amp; PAT integration.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-4 flex flex-col gap-4">
                {modalities.map((item, i) => <div key={item.id} data-index={i} className="p-3 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-sm text-foreground flex items-center gap-1.5">
                        <Waves className="size-3.5 text-primary" />
                        {item.title}
                      </span>
                      <span className="text-[11px] font-mono text-muted-foreground bg-background px-1.5 py-0.5 rounded border border-border">
                        {item.range}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                      {item.applications}
                    </p>
                  </div>)}

                <Separator className="my-1" />

                <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                  <span className="flex items-center gap-1">
                    <FileText className="size-3.5 text-primary" />
                    Method transfer support
                  </span>
                  <Link to="/ProcessAnalyticalTechnologyPage" className="text-primary hover:underline font-medium inline-flex items-center gap-0.5">
                    View PAT Suite
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}